import fscreen from 'fscreen';
import { reduxKey, defaultState } from './constants';
import { getCurrentModal } from '../../Modals/redux/selectors';
import { getIsMobile, getViewMode, getPublicSession } from '../../publicSession/selectors';
import { MeasuredCookieType } from '../../Measured';
import { getCookieSize } from '../../Measured/redux/selectors';
import { DeviationViewMode } from '@wix/da-react-context/pkg/SessionContext';
import ddt from '@wix/da-ddt';
import { getDeviationResourceType, getDeviationType, isLegacyStatus } from '../../DeviationViews/_helpers';
import { getBiEventComponentByType } from './helpers';
import { DeviationTypes } from '../../types/deviation';
import { getIsDuperbrowseActive } from '../../Duperbrowse/redux/selectors';
import { BlockReasons } from '../../Moderation/redux/types';
import { getHighestPriorityBlockReasonForDeviation } from '../../Moderation/redux/selectors';
import { getBasicTierSubscriptionInfo } from '../../utils/hooks/useSubscriptionTier';
import { normalizePremiumFolderData } from '../../utils/normalizePremiumFolderData';
import cssVars from '@wix/da-ds/src/styles/variables.scss';
const logger = ddt.createLogger('devpage');
const isSSR = typeof window === 'undefined';
const FORCED_THEATER_BREAKPOINT = parseInt(cssVars.forceTheaterBreakpoint, 10);
const SIDEBAR_WIDTH = parseInt(cssVars.sidebarWidth, 10);
const DUPER_PADDLE_WIDTH = parseInt(cssVars.duperPaddleWidth, 10);
function getStageSizeFromCookie(state) {
    // get most recent measured cookie for actual stage size
    const cookieSize = getCookieSize(state, MeasuredCookieType.DEVIATION_ARTSTAGE);
    if (!cookieSize || !Array.isArray(cookieSize)) {
        // some arbitrary default size, based on 1280x800 screen,
        // ignoring browser chrome...
        return {
            width: 705,
            height: 680
        };
    }
    const [width, height] = cookieSize;
    return {
        width,
        height
    };
}
function getStoreSlice(state) {
    return state[reduxKey] || defaultState;
}
export function getIsZoomed(state) {
    return getStoreSlice(state).viewOptions.isInZoom;
}
export function getIsDuperbrowseAllowed(state) {
    // if any modal is open, we don't want to move
    if (getCurrentModal(state)) {
        return false;
    }
    // zoom vs duper is messy
    if (getIsZoomed(state)) {
        return false;
    }
    return true;
}
export function getVisiblePanel(state, actionBar = 'primary') {
    if (getStoreSlice(state).viewOptions.activeActionBar !== actionBar) {
        return false;
    }
    return getStoreSlice(state).viewOptions.visiblePanel;
}
export function getIsAnyPanelVisible(state) {
    return !!getStoreSlice(state).viewOptions.visiblePanel;
}
function getScreenSize(state) {
    return getStoreSlice(state).viewOptions.screenSize;
}
export function getIsTheaterForced(state, deviation) {
    var _state_flags, _state_publicSession;
    if (getIsMobile(state)) {
        return true;
    }
    // commission don't have sidebar so they are effectively always theater
    if (deviation && getDeviationType(deviation) === DeviationTypes.COMMISSION) {
        return true;
    }
    if (state === null || state === void 0 ? void 0 : (_state_flags = state.flags) === null || _state_flags === void 0 ? void 0 : _state_flags.ads_min_width) {
        return false;
    }
    if (state === null || state === void 0 ? void 0 : (_state_publicSession = state['@@publicSession']) === null || _state_publicSession === void 0 ? void 0 : _state_publicSession.adsEnabled) {
        return false;
    }
    const screenSize = getScreenSize(state);
    // fall back to guesstimating from stage cookie size in SSR
    const width = screenSize ? screenSize.width : Number(getStageSizeFromCookie(state).width) + DUPER_PADDLE_WIDTH + SIDEBAR_WIDTH;
    // force theater if art stage width + sidebar width < breakpoint
    return width <= FORCED_THEATER_BREAKPOINT;
}
export function getIsFullscreenAllowed(state) {
    if (!isSSR && !fscreen.fullscreenEnabled) {
        return false;
    }
    if (getIsMobile(state)) {
        return false;
    }
    return true;
}
function getIsFullscreen(state) {
    var _fscreen_fullscreenElement;
    const isBrowserFullscreen = !!fscreen.fullscreenElement;
    const isStateFullscreen = getViewMode(state) === DeviationViewMode.Fullscreen;
    // Allow element to declare itself as controlling fullscreen
    if (isBrowserFullscreen && ((_fscreen_fullscreenElement = fscreen.fullscreenElement) === null || _fscreen_fullscreenElement === void 0 ? void 0 : _fscreen_fullscreenElement.dataset.canfs) === 'yes') {
        return false;
    }
    if (isStateFullscreen !== isBrowserFullscreen) {
        // weird shit happens sometimes
        logger.warn('Browser and store fullscreen state have diverged', {
            browser: isBrowserFullscreen,
            store: isStateFullscreen
        });
    }
    // the browser is our real source of truth, store state is just to force react updates
    return isBrowserFullscreen;
}
function getIsTheater(state, deviation) {
    return getIsTheaterForced(state, deviation) || getViewMode(state) === DeviationViewMode.Theater;
}
export function getEffectiveViewMode(state, deviation) {
    if (getIsFullscreen(state)) {
        return DeviationViewMode.Fullscreen;
    }
    // special poll only support normal
    if (deviation && (DeviationTypes.POLL === getDeviationType(deviation) || isLegacyStatus(deviation))) {
        return DeviationViewMode.Normal;
    }
    if (getIsTheater(state)) {
        return DeviationViewMode.Theater;
    }
    return DeviationViewMode.Normal;
}
export function getBiContentViewStatus(state, deviation) {
    if (!deviation) {
        return undefined;
    }
    const biBlockReasons = {
        [BlockReasons.Antisocial]: 'antisocial_logged_out',
        [BlockReasons.MatureLoggedOut]: 'mature_logged_out',
        [BlockReasons.BlockedByArtist]: 'viewer_blocked',
        [BlockReasons.ArtistBlocked]: 'author_blocked',
        [BlockReasons.DeviationBlocked]: 'i_just_dont_want_to_see_this',
        [BlockReasons.StrictMatureAge]: 'strict_mature_underage',
        [BlockReasons.MatureAge]: 'mature_underage',
        [BlockReasons.MatureFilter]: 'mature_filter_on'
    };
    const blockReason = getHighestPriorityBlockReasonForDeviation(state, deviation);
    if (blockReason && biBlockReasons[blockReason] !== undefined) {
        return biBlockReasons[blockReason];
    }
    const { isPremiumLocked , isPaidFolder , isWatchToAccess  } = normalizePremiumFolderData(deviation === null || deviation === void 0 ? void 0 : deviation.premiumFolderData);
    const deviationType = getDeviationType(deviation);
    if (isPremiumLocked && deviationType !== DeviationTypes.LITERATURE) {
        if (isPaidFolder) {
            return 'pg_locked';
        }
        if (isWatchToAccess) {
            return 'watchers_locked';
        }
    }
    const { needsSubscription , isSubscribedButBlocked  } = getBasicTierSubscriptionInfo(deviation);
    if (needsSubscription || isSubscribedButBlocked) {
        return 'subscription_locked';
    }
}
export function getBiEventData(state, deviation) {
    return {
        view: 'submission',
        publicSession: getPublicSession(state),
        eventComponent: getBiEventComponentByType(getDeviationType(deviation)),
        deviationId: deviation.deviationId,
        deviationResourceType: getDeviationResourceType(deviation),
        isDuperbrowse: getIsDuperbrowseActive(state),
        contentViewStatus: getBiContentViewStatus(state, deviation)
    };
}
